import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_EVENTS_QUERY, processEvents, combineAndSortEvents } from '../../../utils/eventUtils';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import Hero from '../../common/Hero';
import ImageSlider from '../../common/Image-slider';
import WhatsOn from '../../common/What-on';
import NewsletterSignUp from '../../common/Newsletter-sign';
import ImageSlant from '../../common/image-slant';
import Footer from '../../Footer/Footer';
import Button from '../../Button/Button';
import christmasVideo from '../../../assets/Christmas_vid.mp4';
import imgFestive from '../../../assets/christmas25/festive.webp';
import imgTea1 from '../../../assets/christmas25/tea1.webp';
import imgTea2 from '../../../assets/christmas25/tea2.webp';
import imgWreath from '../../../assets/christmas25/Wreath.jpg';
import imgGrotto from '../../../assets/christmas25/grotto.webp';
import imgSearch from '../../../assets/christmas25/search.webp';
import retailImage from '../../../assets/christmas-retail-village.png';
import ChristmasAboutModal from '../../common/ChristmasAboutModal';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const imgReindeer = 'https://www.belvoircastle.com/wp/wp-content/uploads/2025/11/reindeer.webp';

const Container = styled.div`
  min-height: 100vh;
  padding-top: 120px;
`;

const HeroSpacer = styled.div`
  height: 240px;
  @media (max-width: 1024px) { height: 200px; }
  @media (max-width: 768px) { height: 0; }
`;

const IntroSection = styled.section`
  position: relative;
  width: calc(100% - 2rem);
  max-width: 1400px;
  margin: 3rem auto 2rem;
  padding: 0;

  @media (max-width: 768px) {
    margin: 2rem auto 1.5rem;
  }
`;

const IntroHeading = styled.h2`
  font-size: 2.8rem;
  margin: 0 0 3rem 0;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.5px;
  line-height: 1.2;
  color: ${props => props.theme.colors.primary};
  text-align: center;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin: 0 0 2rem 0;
  }

  &::after {
    content: '';
    display: block;
    width: 100px;
    height: 4px;
    margin: 1.5rem auto 0;
    border-radius: 2px;
    background: linear-gradient(90deg, transparent, ${props => props.theme.colors.accent}, transparent);
  }
`;

const IntroGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const IntroCard = styled.div.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 40px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  border: 1px solid #f0f0f0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 50px rgba(0,0,0,0.12);
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const CardIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  font-family: ${props => props.theme.fonts.main};
  margin: 0 0 1rem 0;
  text-align: center;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CardText = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #555;
  font-family: ${props => props.theme.fonts.body};
  margin: 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const IntroParagraph = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  margin: 1rem 0 0;
  color: #333;
  font-family: ${props => props.theme.fonts.body};
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const Highlights = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin: 3rem 0 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const HighlightCard = styled.div.attrs({ className: 'bf-surface' })`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 1.5rem;
  border-left: 4px solid ${props => props.theme.colors.accent};
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(5px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.08);
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const HighlightItem = styled.p`
  margin: 0;
  color: #333;
  font-family: ${props => props.theme.fonts.body};
  font-size: 1rem;
  line-height: 1.6;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }

  &::before {
    content: '✨';
    font-size: 1.25rem;
    flex-shrink: 0;
    margin-top: -0.1rem;
  }
`;

// Using shared brand button component instead of a local variant

const Card = styled.div.attrs({ className: 'bf-surface' })`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 8px 20px rgba(0,0,0,0.08);
  margin-bottom: 1.5rem;
`;

const FamilySaverSection = styled.section`
  position: relative;
  width: 100%;
  margin: 3rem 0;
  padding: 0;
  overflow: hidden;

  @media (max-width: 768px) {
    margin: 2rem 0;
  }
`;

const FamilySaverBackground = styled.div`
  position: relative;
  width: calc(100% - 2rem);
  max-width: 1400px;
  margin: 0 auto;
  padding: 3.5rem 2.5rem;
  background:
    linear-gradient(135deg, 
      rgba(197,151,92,0.05) 0%, 
      rgba(197,151,92,0.02) 100%
    ),
    linear-gradient(135deg, #c9985e 0%, ${props => props.theme.colors.accent} 50%, #b8935f 100%);
  border-radius: 20px;
  box-shadow: 
    0 20px 60px rgba(197,151,92,0.3),
    0 0 0 1px rgba(255,255,255,0.15) inset;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem;
    width: calc(100% - 1rem);
  }

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;

    @media (max-width: 768px) {
      width: 300px;
      height: 300px;
      right: -20%;
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -5%;
    width: 500px;
    height: 500px;
    background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;

    @media (max-width: 768px) {
      width: 250px;
      height: 250px;
      left: -15%;
    }
  }
`;

const FamilySaverContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  color: white;
`;

const FamilySaverBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: rgba(255,255,255,0.98);
  color: ${props => props.theme.colors.accent};
  padding: 0.65rem 1.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 1.25rem;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  font-family: ${props => props.theme.fonts.main};
  border: 1px solid rgba(197,151,92,0.2);

  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.55rem 1.4rem;
    gap: 0.5rem;
  }

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    background: ${props => props.theme.colors.accent};
    border-radius: 50%;
    display: block;
  }
`;

const FamilySaverHeading = styled.h2`
  font-size: 2.6rem;
  margin: 0 0 0.75rem 0;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.5px;
  line-height: 1.15;
  color: white;
  text-shadow: 0 2px 10px rgba(0,0,0,0.2);
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin: 0 0 0.75rem 0;
  }
`;

const FamilySaverSubheading = styled.p`
  font-size: 1.2rem;
  line-height: 1.5;
  margin: 0 auto 2rem;
  color: rgba(255,255,255,0.96);
  font-family: ${props => props.theme.fonts.body};
  font-weight: 400;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 0 auto 1.5rem;
  }
`;

const FamilySaverFeatureBox = styled.div.attrs({ className: 'bf-surface' })`
  background: rgba(255,255,255,0.18);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 16px;
  padding: 2rem 2rem;
  margin: 2rem auto;
  max-width: 750px;
  box-shadow: 
    0 10px 40px rgba(0,0,0,0.2),
    inset 0 1px 0 rgba(255,255,255,0.2);
  position: relative;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
    margin: 1.75rem auto;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, 
      transparent, 
      rgba(255,255,255,0.5) 50%, 
      transparent
    );
  }
`;

const FamilySaverHighlightText = styled.p`
  font-size: 1.4rem;
  font-weight: 600;
  color: white;
  margin: 0;
  font-family: ${props => props.theme.fonts.main};
  line-height: 1.5;
  text-shadow: 0 2px 8px rgba(0,0,0,0.2);
  position: relative;
  letter-spacing: 0.3px;

  @media (max-width: 768px) {
    font-size: 1.15rem;
  }
`;

const FamilySaverText = styled.p`
  font-size: 1.1rem;
  line-height: 1.75;
  margin: 1.25rem auto 0;
  color: rgba(255,255,255,0.94);
  font-family: ${props => props.theme.fonts.body};
  max-width: 800px;
  position: relative;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.7;
    margin: 1rem auto 0;
  }
`;

const FamilySaverCTA = styled.div`
  margin-top: 2rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
  }

  button {
    box-shadow: 0 6px 20px rgba(0,0,0,0.25);
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0,0,0,0.3);
    }
  }
`;

// Modal styled components for Reindeer date selection
const ReindeerModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ReindeerModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  position: relative;

  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1rem;
  }
`;

const ReindeerModalTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
`;

const ReindeerModalDescription = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin: 0 0 2rem;
  line-height: 1.6;
`;

const DateOptions = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    gap: 0.75rem;
  }
`;

const DateOption = styled.button`
  padding: 1.5rem;
  border: 2px solid ${props => props.theme.colors.accent};
  border-radius: 8px;
  background: white;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.accent};
    color: white;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 1.25rem;
    font-size: 1rem;
  }
`;

const ReindeerModalCloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  line-height: 1;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const ReindeerModalButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: ${props => props.theme.colors.primary};
  border: 1px solid ${props => props.theme.colors.primary};

  &:hover {
    background: ${props => props.theme.colors.primary};
    color: white;
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.9rem;
  }
`;

const ChristmasHome = () => {
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [showReindeerModal, setShowReindeerModal] = useState(false);
  const { data: allEventsData } = useQuery(GET_EVENTS_QUERY);
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const familySaverBadgeText = isBlackFriday ? 'BLACK FRIDAY' : 'NEW FOR 2025';
  const familySaverHeadingText = isBlackFriday ? 'Black Friday: 20% Off Entry and Santa Tickets + Additional Family Saving' : 'Opening Our Doors Wider Than Ever';
  const familySaverSubheadingText = isBlackFriday
    ? "Secure your Christmas visit and unlock an automatic 20% saving on Entry and Santa Tickets when you build your Family Saver party."
    : "This Christmas, we're delighted to introduce our revolutionary Family Saver booking system";
  const familySaverHighlightCopy = isBlackFriday
    ? '20% off Entry and Santa Tickets plus additional Family Saver savings when you book during Black Friday.'
    : 'The more people you add, the more you save';
  const familySaverButtonLabel = isBlackFriday ? 'Unlock 20% Off' : 'Discover Your Family Savings';
  
  // Hardcoded Christmas events
  const hardcodedChristmasEvents = [
    {
      id: null,
      title: "Santa's Grotto",
      date: 'Saturday, November 29, 2025 to Wednesday, December 31, 2025',
      description: "Meet Santa in his grotto at Belvoir Retail Village. A magical moment for families.",
      image: imgGrotto,
      location: 'Retail Village',
      link: 'https://www.belvoircastle.com/christmas/santas-grotto',
      linkText: "Explore Santa's Grotto",
      isFlagship: true,
      eventTags: ['christmas', 'family', 'santa'],
      eventDetails: { eventDate: '2025-11-29', eventEndDate: '2025-12-31' }
    },
    {
      id: null,
      title: 'Search for Santa',
      date: 'Saturday, November 29, 2025 to Wednesday, December 31, 2025 (weekends only)',
      description: 'Exclusive early morning family trail through the Castle to find Santa. Weekends only. Includes snacks, drinks and full-day access.',
      image: imgSearch,
      location: 'Castle',
      link: '/christmas/search-for-santa',
      linkText: 'Discover Search for Santa',
      isFlagship: true,
      eventTags: ['christmas', 'family', 'trail'],
      eventDetails: { eventDate: '2025-11-29', eventEndDate: '2025-12-31' }
    },
    {
      id: null,
      title: 'Festive Mice Trail',
      date: 'Saturday, November 29, 2025 to Wednesday, December 31, 2025',
      description: 'Follow the delightful Mice Trail through the Castle\'s grand rooms. Included with Entry.',
      image: imgFestive,
      location: 'Castle',
      link: '/christmas/mice-trail',
      linkText: 'Explore the Mice Trail',
      eventTags: ['christmas', 'family', 'included'],
      eventDetails: { eventDate: '2025-11-29', eventEndDate: '2025-12-31' }
    },
    {
      id: null,
      title: 'Wreath Making Workshop at Belvoir Bistro',
      date: 'Thursday, November 27, 2025, 6:00 PM',
      description: '£60 per person. 20 spaces per event. Includes all materials, a glass of bubbles and a mince pie. Hosted by Sarah Youngman (Sage and Satin Florals).',
      image: imgWreath,
      location: 'Retail Village',
      link: '/christmas/wreath-making',
      linkText: 'Learn More',
      eventTags: ['workshop', 'limited', 'christmas'],
      eventDetails: { eventDate: '2025-11-27T18:00:00' }
    },
    {
      id: null,
      title: 'Wreath Making Workshop at Belvoir Bistro',
      date: 'Friday, December 5, 2025, 6:00 PM',
      description: '£60 per person. 20 spaces per event. Includes all materials, a glass of bubbles and a mince pie. Hosted by Sarah Youngman (Sage and Satin Florals).',
      image: imgWreath,
      location: 'Retail Village',
      link: '/christmas/wreath-making',
      linkText: 'Learn More',
      eventTags: ['workshop', 'limited', 'christmas'],
      eventDetails: { eventDate: '2025-12-05T18:00:00' }
    }
  ];
  
  // Combine hardcoded events with WordPress events
  const allEvents = React.useMemo(() => {
    const wordpressEvents = allEventsData?.events?.nodes || [];
    return combineAndSortEvents(hardcodedChristmasEvents, wordpressEvents);
  }, [allEventsData]);
  
  // Handler for Meet Santa's Reindeer - shows modal with date options
  const handleReindeerClick = () => {
    setShowReindeerModal(true);
  };

  const handleDateSelection = (date) => {
    if (date === '13th') {
      window.location.assign('https://www.belvoircastle.com/event/cG9zdDozNDc=');
    } else if (date === '20th') {
      window.location.assign('https://www.belvoircastle.com/event/cG9zdDozNDk=');
    }
    setShowReindeerModal(false);
  };

  const handleCloseReindeerModal = () => {
    setShowReindeerModal(false);
  };
  
  const slides = [
    { image: imgFestive, title: 'Your Christmas Visit', action: () => window.location.assign('/christmas/your-festive-visit') },
    { image: imgTea1, title: 'Festive Afternoon Tea', action: () => window.location.assign('/christmas/festive-afternoon-tea') },
    { image: imgGrotto, title: "Santa's Grotto", action: () => window.location.assign('https://www.belvoircastle.com/christmas/santas-grotto') },
    { image: imgSearch, title: 'Search for Santa', action: () => window.location.assign('/christmas/search-for-santa') },
    { image: imgReindeer, title: "Meet Santa's Reindeer", action: handleReindeerClick },
    { image: retailImage, title: 'Christmas Shopping', action: () => window.location.assign('https://www.belvoircastle.com/retail-village') },
  ];

  // Christmas events data for structured data
  const christmasEvents = [
    {
      "@type": "Event",
      "name": "Christmas at Belvoir Castle 2025",
      "description": "Experience a magical Christmas at Belvoir Castle with festive displays, Santa's Grotto, afternoon tea, and family activities in Leicestershire.",
      "startDate": "2025-11-29",
      "endDate": "2025-12-30",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": "Belvoir Castle",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Belvoir Castle",
          "addressLocality": "Grantham",
          "addressRegion": "Leicestershire",
          "postalCode": "NG32 1PE",
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 52.8931,
          "longitude": -0.7792
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "Belvoir Castle",
        "url": "https://www.belvoircastle.com"
      },
      "offers": {
        "@type": "Offer",
        "url": "https://www.belvoircastle.com/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/InStock"
      },
      "image": "https://www.belvoircastle.com/christmas25/festive.webp"
    },
    {
      "@type": "Event",
      "name": "Santa's Grotto at Belvoir Retail Village",
      "description": "Meet Santa in his magical grotto at Belvoir Retail Village. A magical moment for families this Christmas.",
      "startDate": "2025-11-29",
      "endDate": "2025-12-31",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": "Belvoir Castle",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Belvoir Castle",
          "addressLocality": "Grantham",
          "addressRegion": "Leicestershire",
          "postalCode": "NG32 1PE",
          "addressCountry": "GB"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "Belvoir Castle",
        "url": "https://www.belvoircastle.com"
      },
      "image": "https://www.belvoircastle.com/christmas25/grotto.webp"
    },
    {
      "@type": "Event",
      "name": "Search for Santa - Belvoir Castle",
      "description": "Exclusive early morning family trail through the Castle to find Santa. Weekends only. Includes snacks, drinks and full-day access.",
      "startDate": "2025-11-29",
      "endDate": "2025-12-31",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": "Belvoir Castle",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Belvoir Castle",
          "addressLocality": "Grantham",
          "addressRegion": "Leicestershire",
          "postalCode": "NG32 1PE",
          "addressCountry": "GB"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "Belvoir Castle",
        "url": "https://www.belvoircastle.com"
      },
      "image": "https://www.belvoircastle.com/christmas25/search.webp"
    },
    {
      "@type": "Event",
      "name": "Wreath Making Workshop at Belvoir Castle",
      "description": "£60 per person. 20 spaces per event. Includes all materials, a glass of bubbles and a mince pie. Hosted by Sarah Youngman (Sage and Satin Florals).",
      "startDate": "2025-11-27T18:00:00",
      "endDate": "2025-11-27T21:00:00",
      "eventStatus": "https://schema.org/EventScheduled",
      "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
      "location": {
        "@type": "Place",
        "name": "Belvoir Bistro",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Belvoir Castle",
          "addressLocality": "Grantham",
          "addressRegion": "Leicestershire",
          "postalCode": "NG32 1PE",
          "addressCountry": "GB"
        }
      },
      "organizer": {
        "@type": "Organization",
        "name": "Belvoir Castle",
        "url": "https://www.belvoircastle.com"
      },
      "offers": {
        "@type": "Offer",
        "price": "60",
        "priceCurrency": "GBP",
        "availability": "https://schema.org/LimitedAvailability"
      },
      "image": "https://www.belvoircastle.com/christmas25/Wreath.jpg"
    }
  ];

  // Main organization structured data
  const organizationData = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": "Belvoir Castle",
    "description": "Historic castle offering Christmas events, Santa's Grotto, festive afternoon tea, and family activities in Leicestershire and Lincolnshire border.",
    "url": "https://www.belvoircastle.com/christmas",
    "isAccessibleForFree": false,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Belvoir Castle",
      "addressLocality": "Grantham",
      "addressRegion": "Leicestershire",
      "postalCode": "NG32 1PE",
      "addressCountry": "UK"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 52.8931,
      "longitude": -0.7792
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday", 
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ],
      "opens": "10:00",
      "closes": "17:00",
      "validFrom": "2025-11-29",
      "validThrough": "2025-12-31"
    },
    "priceRange": "££",
    "telephone": "+441476871001",
    "email": "info@belvoircastle.com",
    "image": "https://www.belvoircastle.com/christmas25/festive.webp",
    "sameAs": [
      "https://www.facebook.com/belvoircastle",
      "https://www.instagram.com/belvoircastle",
      "https://twitter.com/belvoircastle"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Christmas Events 2025",
      "itemListElement": christmasEvents
    }
  };

  return (
    <Container className={pageClassName}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Christmas at Belvoir Castle 2025 | Santa's Grotto & Events</title>
        <meta name="title" content="Christmas at Belvoir Castle 2025 | Santa's Grotto & Events" />
        <meta name="description" content="Experience magical Christmas at Belvoir Castle 2025. Santa's Grotto, festive afternoon tea, Christmas events & family activities in Leicestershire & Lincolnshire border. Book now!" />
        <meta name="keywords" content="Christmas at Belvoir Castle, Christmas events Leicestershire, Santa's Grotto, Christmas afternoon tea, Christmas family activities, Christmas castle events, festive events Lincolnshire, Christmas day out Leicestershire, Christmas activities near me, Belvoir Castle Christmas 2025" />
        <meta name="author" content="Belvoir Castle" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="geo.region" content="GB-LEI" />
        <meta name="geo.placename" content="Grantham, Leicestershire" />
        <meta name="geo.position" content="52.8931;-0.7792" />
        <meta name="ICBM" content="52.8931, -0.7792" />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas" />
        <meta property="og:title" content="Christmas at Belvoir Castle 2025 | Santa's Grotto & Festive Events" />
        <meta property="og:description" content="Experience magical Christmas at Belvoir Castle 2025. Santa's Grotto, festive afternoon tea, Christmas events & family activities in Leicestershire. Book your Christmas visit today!" />
        <meta property="og:image" content="https://www.belvoircastle.com/christmas25/festive.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.belvoircastle.com/christmas" />
        <meta name="twitter:title" content="Christmas at Belvoir Castle 2025 | Santa's Grotto & Festive Events" />
        <meta name="twitter:description" content="Experience magical Christmas at Belvoir Castle 2025. Santa's Grotto, festive afternoon tea, Christmas events & family activities in Leicestershire." />
        <meta name="twitter:image" content="https://www.belvoircastle.com/christmas25/festive.webp" />
        <meta name="twitter:site" content="@belvoircastle" />
        <meta name="twitter:image:alt" content="Christmas at Belvoir Castle" />

        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#101d40" />
        <meta name="msapplication-TileColor" content="#101d40" />
        <meta name="apple-mobile-web-app-title" content="Christmas at Belvoir Castle" />
        <meta name="application-name" content="Belvoir Castle Christmas" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationData)}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.belvoircastle.com"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Christmas 2025",
                "item": "https://www.belvoircastle.com/christmas"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Hero 
        videoSrc={christmasVideo}
        welcomeText="This Christmas at"
        mainTitle="Belvoir Castle"
        subtitle="A Magical Night Before Christmas"
        darkOverlay={isBlackFriday}
        buttons={[
          { text: 'Book Now', onClick: () => window.location.assign('/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14') },
          { text: 'Your Christmas Visit', onClick: () => window.location.assign('/christmas/your-festive-visit') }
        ]}
        infoColumns={[
          { title: 'Opening Times', items: ['29 November - 30 December', '10am - 5pm'] },
          { 
            title: 'Afternoon Tea', 
            items: [
              'Seasonal menus', 
              { text: 'Festive Afternoon Tea', link: '/christmas/festive-afternoon-tea' }
            ] 
          },
          { 
            title: 'Mice Trail', 
            items: [
              'Included with Entry', 
              { text: 'Family-friendly trail', link: '/christmas/mice-trail' }
            ] 
          },
          { 
            title: "Meet Santa", 
            items: [
              { text: "Santa's Grotto", link: 'https://www.belvoircastle.com/christmas/santas-grotto' }, 
              { text: 'Search for Santa', link: '/christmas/search-for-santa' }
            ] 
          },
        
        ]}
      />

      <ImageSlider slides={slides} darkMode={isBlackFriday} />
      
      <FamilySaverSection>
        <FamilySaverBackground>
          <FamilySaverContent>
            <FamilySaverBadge>{familySaverBadgeText}</FamilySaverBadge>
            <FamilySaverHeading>
              {familySaverHeadingText}
            </FamilySaverHeading>
            <FamilySaverSubheading>
              {familySaverSubheadingText}
            </FamilySaverSubheading>
            <FamilySaverFeatureBox>
              <FamilySaverHighlightText>
                {familySaverHighlightCopy}
              </FamilySaverHighlightText>
            </FamilySaverFeatureBox>
            {isBlackFriday && (
              <FamilySaverText>
                Book your tickets between Black Friday and Cyber Monday to activate the 20% saving on Entry and Santa Tickets. The Family Saver tiers stack automatically, so the bigger your visiting party, the bolder the reduction.
              </FamilySaverText>
            )}
            <FamilySaverText>
              We believe Christmas magic should be shared with everyone you love. That's why we've created something truly special — a first-of-its-kind booking experience designed to reward families of all sizes. Simply start building your group and watch what happens. Add your partner, your children, grandparents, friends... the possibilities are endless, and so are the rewards.
            </FamilySaverText>
            <FamilySaverText>
              Don't just take our word for it — discover it for yourself. Play around with our booking system, add more people to your group, and see the magic unfold. This is our way of welcoming more families through our historic doors this festive season.
            </FamilySaverText>
            <FamilySaverCTA>
              <Button onClick={() => window.location.assign('/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14')}>
                {familySaverButtonLabel}
              </Button>
            </FamilySaverCTA>
          </FamilySaverContent>
        </FamilySaverBackground>
      </FamilySaverSection>

      <IntroSection>
        <IntroHeading>
          Your Magical Christmas Experience
        </IntroHeading>
        
        <IntroGrid>
          <IntroCard>
            <CardIcon>🏰</CardIcon>
            <CardTitle>Historic Castle Magic</CardTitle>
            <CardText>
              Step into a Christmas like no other at Belvoir Castle. Our historic estate transforms into a winter wonderland filled with sparkling lights, storybook décor and heartwarming experiences for all ages.
            </CardText>
          </IntroCard>
          
          <IntroCard>
            <CardIcon>🐭</CardIcon>
            <CardTitle>New Mice Trail</CardTitle>
            <CardText>
              New for 2025! Follow our beloved Three Mice through the Castle's grand rooms to uncover tiny surprises around every corner — a charming, interactive adventure for children to explore.
            </CardText>
          </IntroCard>
          
          <IntroCard>
            <CardIcon>🎅</CardIcon>
            <CardTitle>Meet Santa</CardTitle>
            <CardText>
              Create unforgettable memories with Santa's Grotto or join the exclusive Search for Santa trail. Perfect family Christmas activities in the heart of Leicestershire.
            </CardText>
          </IntroCard>
        </IntroGrid>

        <Highlights>
          <HighlightCard>
            <HighlightItem>Enchanting Christmas castle displays brimming with festive storytelling and seasonal décor</HighlightItem>
          </HighlightCard>
          <HighlightCard>
            <HighlightItem>Festive Afternoon Tea featuring indulgent Christmas treats and winter flavours</HighlightItem>
          </HighlightCard>
          <HighlightCard>
            <HighlightItem>Beautiful Christmas parkland and grounds perfect for winter walks</HighlightItem>
          </HighlightCard>
          <HighlightCard>
            <HighlightItem>Christmas shopping and festive gifts at the Belvoir Retail Village</HighlightItem>
          </HighlightCard>
          <HighlightCard>
            <HighlightItem>Over 900 years of family Christmas celebrations in England's most beautiful castle</HighlightItem>
          </HighlightCard>
          <HighlightCard>
            <HighlightItem>The ultimate Christmas destination for festive celebrations in Leicestershire</HighlightItem>
          </HighlightCard>
        </Highlights>

        <div style={{ margin: '3rem auto 0', display: 'flex', justifyContent: 'center' }}>
          <Button onClick={() => window.location.assign('/booking-journey?locationId=978&pageId=278ef0c8-a65b-4e31-9726-305ae06a5a14')}>
            Book Your Christmas Visit
          </Button>
        </div>
      </IntroSection>

      <WhatsOn 
        title="Christmas Events & Activities at Belvoir Castle 2025"
        enableScrolling={true}
        events={allEvents}
        showParklandSection={false}
        darkMode={isBlackFriday}
      />
      
      <NewsletterSignUp tag="Christmas 2025" />

      <ImageSlant 
        image={imgTea2}
        title="Christmas Afternoon Tea at Belvoir Castle"
        description="Indulge in a festive selection of Christmas sweet and savoury treats in our elegant castle tearooms. Perfect Christmas dining experience in Leicestershire."
        buttonText="Book Christmas Afternoon Tea"
        buttonAction={() => window.location.assign('/christmas/festive-afternoon-tea')}
        darkMode={isBlackFriday}
        imagePosition="left"
      />

      <ImageSlant 
        image={imgGrotto}
        title="Meet Santa at Belvoir Castle Christmas"
        description="Create magical Christmas memories with a visit to Santa's Grotto or join the Search for Santa Christmas trail. Best Christmas family activities in Leicestershire."
        buttonText="Explore Christmas Santa Experiences"
        buttonAction={() => window.location.assign('https://www.belvoircastle.com/christmas/santas-grotto')}
        darkMode={isBlackFriday}
        imagePosition="right"
      />
      <Footer />
      <ChristmasAboutModal 
        isOpen={isAboutModalOpen} 
        onClose={() => setIsAboutModalOpen(false)} 
      />
      
      {showReindeerModal && (
        <ReindeerModal onClick={handleCloseReindeerModal}>
          <ReindeerModalContent onClick={(e) => e.stopPropagation()}>
            <ReindeerModalCloseButton onClick={handleCloseReindeerModal}>
              &times;
            </ReindeerModalCloseButton>
            <ReindeerModalTitle>Meet Santa's Reindeer</ReindeerModalTitle>
            <ReindeerModalDescription>
              Choose your preferred date for meeting Santa's reindeer:
            </ReindeerModalDescription>

            <DateOptions>
              <DateOption onClick={() => handleDateSelection('13th')}>
                13th December
              </DateOption>

              <DateOption onClick={() => handleDateSelection('20th')}>
                20th December
              </DateOption>
            </DateOptions>

            <ReindeerModalButton onClick={handleCloseReindeerModal}>
              Cancel
            </ReindeerModalButton>
          </ReindeerModalContent>
        </ReindeerModal>
      )}
    </Container>
  );
};

export default ChristmasHome;

